/** @jsxImportSource @emotion/react */
'use client'

import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { FlexCol } from '../../layout'
import { styles } from './styles'

/** Must stay in sync with backend OTP_TTL_SECONDS. */
export const OTP_TIMEOUT_SECONDS = 120

function formatRemaining(seconds: number): string {
  const clamped = Math.max(0, seconds)
  const minutes = Math.floor(clamped / 60)
  const remainder = clamped % 60
  return `${minutes}:${remainder.toString().padStart(2, '0')}`
}

export interface OtpFormProps {
  otpCode: string
  onOtpChange: (value: string) => void
  onSubmit: () => void
  onBack: () => void
  onTimeout: () => void
  otpError: string | null
  isLoading: boolean
}

export default function OtpForm({
  otpCode,
  onOtpChange,
  onSubmit,
  onBack,
  onTimeout,
  otpError,
  isLoading,
}: OtpFormProps) {
  const [remainingSeconds, setRemainingSeconds] = useState(OTP_TIMEOUT_SECONDS)
  const timedOutRef = useRef(false)

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setRemainingSeconds((current) => (current <= 1 ? 0 : current - 1))
    }, 1000)
    return () => window.clearInterval(intervalId)
  }, [])

  useEffect(() => {
    if (remainingSeconds > 0 || timedOutRef.current) {
      return
    }
    timedOutRef.current = true
    onTimeout()
  }, [remainingSeconds, onTimeout])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (remainingSeconds <= 0) {
      return
    }
    onSubmit()
  }

  const expired = remainingSeconds <= 0

  return (
    <form onSubmit={handleSubmit} noValidate>
      <FlexCol gap="lg">
        <FlexCol gap="xs">
          <Typography
            variant="h1"
            component="h1"
            css={[styles.headingFont, styles.heading]}
          >
            Enter your code
          </Typography>
          <Typography variant="body1" css={styles.lede}>
            We&apos;ve sent a one-time passcode to your email. Enter it below to
            continue.
          </Typography>
          <Typography variant="body2" css={styles.lede} aria-live="polite">
            Code expires in {formatRemaining(remainingSeconds)}
          </Typography>
        </FlexCol>

        <FlexCol gap="md">
          <div css={styles.field}>
            <Typography component="label" htmlFor="otp_code" css={styles.label}>
              One-time passcode
            </Typography>
            <TextField
              id="otp_code"
              hiddenLabel
              placeholder="123456"
              fullWidth
              variant="outlined"
              autoComplete="one-time-code"
              inputMode="numeric"
              value={otpCode}
              onChange={(event) => onOtpChange(event.target.value)}
              disabled={expired}
            />
          </div>

          {otpError && (
            <Typography role="alert" color="error" variant="body2">
              {otpError}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isLoading || expired}
            css={styles.primaryButton}
          >
            Continue
          </Button>
          <Button
            type="button"
            variant="text"
            onClick={onBack}
            css={styles.secondaryButton}
          >
            Back to sign in
          </Button>
        </FlexCol>
      </FlexCol>
    </form>
  )
}
